import React from 'react';
import { getVariableLabel } from '../../../utils/skillConstants';

/* ─── interface-design decisions ────────────────────────────────────────────
   Domain: sala de análisis técnico de carrera
   - Effects: verde pasto (activación positiva)
   - Conditions: azul cielo (filtros de fase de carrera)
   - Preconditions: ámbar (requisito previo — más restrictivo, más caliente)
   - Chips de condición: signature element del proyecto
     No son texto plano — son "nodos de activación" codificados por tipo.
     El operador (==, <=, >=) es visible pero en menor jerarquía.
     El label semántico (Front Runner, Long) es la recompensa de leerlo.
   - JetBrains Mono para nombres de variable + valores: enfatiza que son
     identificadores técnicos, no texto en lenguaje natural.
*/

/* ─── Condition Chip ─────────────────────────────────────────────────────── */
const ConditionChip = ({ variable, operator, value, theme }) => {
    const label = getVariableLabel(variable, value);

    /* Temas por tipo de condición — interface-design: color carries meaning */
    const themes = {
        condition: {
            wrap: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/70 text-blue-900 dark:text-blue-100',
            var: 'text-blue-600 dark:text-blue-300',
            op: 'text-blue-400 dark:text-blue-500',
            val: 'text-blue-700 dark:text-blue-200',
            sep: 'text-blue-300 dark:text-blue-700',
            label: 'text-blue-500 dark:text-blue-400',
        },
        precondition: {
            wrap: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/70 text-amber-900 dark:text-amber-100',
            var: 'text-amber-600 dark:text-amber-300',
            op: 'text-amber-400 dark:text-amber-500',
            val: 'text-amber-700 dark:text-amber-200',
            sep: 'text-amber-300 dark:text-amber-700',
            label: 'text-amber-500 dark:text-amber-400',
        },
    };
    const t = themes[theme] || themes.condition;

    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg
            border text-[12px] ${t.wrap} whitespace-nowrap`}>
            <span className={`font-mono font-semibold tracking-tight ${t.var}`}>{variable}</span>
            <span className={`font-mono ${t.op}`}>{operator}</span>
            <span className={`font-mono font-bold ${t.val}`}>{value}</span>
            {label && (
                <>
                    <span className={`mx-0.5 ${t.sep}`}>·</span>
                    <span className={`font-sans font-medium text-[11px] ${t.label}`}>{label}</span>
                </>
            )}
        </span>
    );
};

/* ─── Condition Section (Conditions or Preconditions) ───────────────────── */
const ConditionSection = ({ title, conditions, theme }) => {
    if (!conditions || conditions.length === 0) return null;

    const isPrecon = theme === 'precondition';
    const headerAccent = isPrecon
        ? 'text-amber-500 dark:text-amber-400'
        : 'text-blue-500 dark:text-blue-400';
    const barColor = isPrecon
        ? 'bg-amber-400 dark:bg-amber-500'
        : 'bg-blue-400 dark:bg-blue-500';

    const allConds = conditions.flatMap(obj =>
        Object.entries(obj).map(([variable, ops]) => ({ variable, ops }))
    );

    return (
        <div className="space-y-2.5">
            {/* Header con barra coloreada — interface-design: border progression */}
            <div className="flex items-center gap-2">
                <div className={`w-0.5 h-4 rounded-full ${barColor}`} />
                <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${headerAccent}`}>
                    {title}
                </span>
            </div>

            {/* Chips — data-visualization: Follow Through: stagger via CSS delay */}
            <div className="flex flex-wrap gap-1.5 pl-3">
                {allConds.map(({ variable, ops }, i) => {
                    if (ops.length === 1) {
                        const [operator, value] = ops[0];
                        return (
                            <ConditionChip
                                key={`${variable}-${i}`}
                                variable={variable}
                                operator={operator}
                                value={value}
                                theme={theme}
                            />
                        );
                    }
                    /* Rango AND: dos condiciones sobre la misma variable */
                    const isPreconTheme = theme === 'precondition';
                    const wrapCls = isPreconTheme
                        ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/70 text-amber-900 dark:text-amber-100'
                        : 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/70 text-blue-900 dark:text-blue-100';
                    const varCls = isPreconTheme ? 'text-amber-600 dark:text-amber-300' : 'text-blue-600 dark:text-blue-300';
                    const opCls = isPreconTheme ? 'text-amber-400 dark:text-amber-500' : 'text-blue-400 dark:text-blue-500';
                    const valCls = isPreconTheme ? 'text-amber-700 dark:text-amber-200' : 'text-blue-700 dark:text-blue-200';
                    const sepCls = isPreconTheme ? 'text-amber-300 dark:text-amber-700' : 'text-blue-300 dark:text-blue-700';

                    return (
                        <span key={`${variable}-${i}`}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[12px] font-mono ${wrapCls} whitespace-nowrap`}>
                            <span className={`font-semibold ${varCls}`}>{variable}</span>
                            {ops.map(([op, val], j) => (
                                <React.Fragment key={j}>
                                    {j > 0 && <span className={`text-[10px] font-sans mx-0.5 font-bold ${sepCls}`}>AND</span>}
                                    <span className={opCls}>{op}</span>
                                    <span className={`font-bold ${valCls}`}>{val}</span>
                                </React.Fragment>
                            ))}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Effects Section ─────────────────────────────────────────────────────── */
const EffectsSection = ({ effects }) => {
    if (!effects || Object.keys(effects).length === 0) return null;

    return (
        <div className="space-y-2.5">
            {/* Header verde — effects = activación positiva */}
            <div className="flex items-center gap-2">
                <div className="w-0.5 h-4 rounded-full bg-green-500 dark:bg-green-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-green-600 dark:text-green-400">
                    Effects
                </span>
            </div>

            {/* Tabla compacta — JetBrains Mono: valores técnicos en monospace */}
            <div className="pl-3 overflow-x-auto">
                <table className="text-sm border-collapse w-auto">
                    <tbody>
                        {Object.entries(effects).map(([effectName, entries]) =>
                            entries.map(([value, magnitude, description], i) => (
                                <tr key={`${effectName}-${i}`}
                                    className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                                    <td className="pr-8 py-2 font-semibold text-gray-700 dark:text-gray-200
                                        text-[13px] tracking-tight whitespace-nowrap">
                                        {effectName}
                                    </td>
                                    <td className="py-2 font-bold text-green-700 dark:text-green-300
                                        whitespace-nowrap"
                                        style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                        {value !== '' && value !== null ? (
                                            <>
                                                {value}
                                                {magnitude && magnitude !== '' && (
                                                    <span className="text-green-500 dark:text-green-400 text-[11px] ml-0.5">
                                                        {magnitude}
                                                    </span>
                                                )}
                                            </>
                                        ) : '—'}
                                    </td>
                                    {description && description !== '' && (
                                        <td className="pl-4 py-2 text-[12px] text-gray-400 dark:text-gray-500 italic whitespace-nowrap">
                                            {description}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */

/**
 * SkillEffectGroup — Un grupo de skill_effects (Effects + Conditions + Preconditions).
 * Props: group { group_id, effects, Conditions, Preconditions }, isFirst
 */
const SkillEffectGroup = ({ group, isFirst }) => {
    const { group_id, effects, Conditions, Preconditions } = group;

    const hasEffects = effects && Object.keys(effects).length > 0;
    const hasConditions = Conditions && Conditions.length > 0;
    const hasPreconditions = Preconditions && Preconditions.length > 0;

    return (
        <div className="rounded-2xl overflow-hidden
            border border-gray-150 dark:border-gray-700
            bg-white dark:bg-gray-800
            shadow-sm">

            {/* Header del grupo — fondo levemente separado del cuerpo */}
            <div className="flex items-center gap-3 px-5 py-3
                bg-gray-50/80 dark:bg-gray-800/80
                border-b border-gray-100 dark:border-gray-700">
                <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                    Scenario
                </span>
                <span className="text-sm font-bold text-gray-700 dark:text-gray-200"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {group_id}
                </span>
            </div>

            {/* Cuerpo: secciones separadas visualmente */}
            <div className="px-5 py-5 space-y-5">

                {hasEffects && <EffectsSection effects={effects} />}

                {/* Separador entre effects y condiciones */}
                {hasEffects && (hasConditions || hasPreconditions) && (
                    <div className="border-t border-gray-100 dark:border-gray-700" />
                )}

                {/* Preconditions primero — más restrictivas, van arriba */}
                {hasPreconditions && (
                    <ConditionSection
                        title="Preconditions"
                        conditions={Preconditions}
                        theme="precondition"
                    />
                )}

                {/* Separador entre preconditions y conditions si ambas existen */}
                {hasPreconditions && hasConditions && (
                    <div className="border-t border-gray-100 dark:border-gray-700" />
                )}

                {hasConditions && (
                    <ConditionSection
                        title="Conditions"
                        conditions={Conditions}
                        theme="condition"
                    />
                )}

                {!hasEffects && !hasConditions && !hasPreconditions && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic text-center py-2">
                        No data available for this scenario.
                    </p>
                )}
            </div>
        </div>
    );
};

export default SkillEffectGroup;
